import type { ReactNode } from 'react';
import { Stack, TokenBox } from '../ui/Layout';

// ------------------------------------------------------------------
// AppFrame — shell layout-slot primitive (story 2-127; UI-DS-00 §3.1 /
// 2026-08-22-app-shell-ui-refinement-design.md §2-3).
// ------------------------------------------------------------------
//
// The visual model this replaces the shell's current fixed-sidebar-plus-
// padding layout with (`AppShell.tsx`'s `sm:pl-72` + `main.mx-auto.max-w-6xl`
// today): a three-column rhythm — `Rail` (narrow, low-contrast, persistent
// orientation), `Canvas` (the primary reading surface, width-clamped for
// readability), and an optional `Panel` (secondary grouped information,
// stronger separation by size and elevation — history, access, summary).
//
// This is a LAYOUT SLOT, not a shell composition (mapping doc §5's "shared
// layout is a slot contract, not a shell edit" rule): it takes rail/panel
// content as props/children and owns none of it — no knowledge of
// `Sidebar`, `TopBar`, or routing. Composing `AppFrame` around the real
// `Sidebar`/`TopBar`/`Outlet` is `AppShell.tsx`'s job, wired in story 2-128;
// this story ships the primitive and its gallery demo only.
//
// DOM/visual order is deliberately the same thing (rail, then canvas
// children, then panel, all in source order via a plain flex row) — no
// CSS-order trick reshuffles focus away from reading order, so Tab visits
// rail → canvas → panel exactly as sighted users scan it (the a11y pass
// this story's done-when calls for). Slot CONTAINERS get the rail/canvas/
// panel surface treatment (background, hairline border, width); the
// SEMANTIC landmark elements (`<nav>`, `<main>`, `<aside>`) stay the
// caller's choice — a layout-slot primitive imposing `<main>` on content
// it didn't write risks a duplicate landmark once a real page's own
// `<main>` is composed in.
export interface AppFrameProps {
  /** Persistent rail/orientation slot. Omit for a canvas-only (or canvas+panel) layout. */
  rail?: ReactNode;
  /** Secondary column — `RailPanel` stack (version history, share access, summary). */
  panel?: ReactNode;
  /** Primary reading surface. */
  children: ReactNode;
  /** Rail column width. Defaults to the existing shell's own rail proportion (18rem / `w-72`) so a later swap-in doesn't shift anything. */
  railWidth?: string;
  /** Panel column width. Defaults to `var(--panel-w)` (20rem / 320px, `tokens.css`, story 2-147xd) — the width the report-rail share-link cards are sized for (2026-08-22 spec §4.4); this prop predates the token (a literal `'20rem'` default) and is the real second consumer that justified minting it, alongside `ReportDetailPage.tsx`'s own 320px rail split (`Split`'s `ratio="panel"`). */
  panelWidth?: string;
  /** Max width clamp applied to the canvas's own inner content, centered — independent of the outer rail/panel columns. Defaults to 72rem (Tailwind's `max-w-6xl`, matching `AppShell`'s current canvas width). */
  contentMaxWidth?: string;
  className?: string;
  'data-testid'?: string;
  /**
   * Extra classes merged onto the rail column's own wrapper `div` — story
   * 2-146c's first real (non-shell) rail consumer, `CapturePage`, needs a
   * responsive `hidden lg:block` here: AppFrame itself has no breakpoint
   * opinion (rail/panel widths are fixed, not responsive), and a persistent
   * side column has nowhere to go at a phone width. The column CONTENT is
   * still what decides whether a mobile drawer/sheet fallback exists (same
   * "one list, two chromes" pattern `SectionNav`/`TemplateEditorPage`'s tab
   * switcher already use) — this prop only controls whether the COLUMN
   * ITSELF is painted at the current breakpoint. Omit for the existing
   * always-visible rail (TemplateEditorPage/gallery demo — unchanged).
   */
  railClassName?: string;
  /** Same as `railClassName`, for the panel column. */
  panelClassName?: string;
  /**
   * False when this AppFrame is nested INSIDE another chrome that already
   * supplies the canvas's own outer padding/max-width — story 2-146c:
   * `CapturePage` is a shell-routed page, so it already renders inside
   * `AppShell`'s own top-level `AppFrame` (rail = `Sidebar`), which already
   * clamps and pads the canvas it hands to the routed page. A second,
   * literal `px-4 py-6 sm:px-8`/`mx-auto max-w-*` wrapper here would double
   * both. `TemplateEditorPage` (the outside-AppShell, full-screen case
   * 2-142 shipped) is unaffected — it keeps the default (`true`), which is
   * the ONLY behavior every existing consumer (that page, the gallery demo)
   * has ever exercised.
   */
  padded?: boolean;
}

export function AppFrame({
  rail,
  panel,
  children,
  railWidth = '18rem',
  panelWidth = 'var(--panel-w)',
  contentMaxWidth = '72rem',
  className = '',
  'data-testid': testId,
  railClassName = '',
  panelClassName = '',
  padded = true,
}: AppFrameProps): JSX.Element {
  const canvas = padded ? (
    <TokenBox maxWidth={contentMaxWidth} className="mx-auto w-full px-4 py-6 sm:px-8">
      <Stack gap={6}>{children}</Stack>
    </TokenBox>
  ) : (
    children
  );
  return (
    // `h-full` rather than `min-h-screen`: AppFrame sizes to whatever height
    // its parent gives it (the real shell, when 2-128 wires this in, will be
    // the full-viewport container; the gallery demo below wraps it in a
    // fixed-height box so it reads as one bounded example among many
    // Section()s rather than blowing out the page).
    <div className={`flex h-full w-full items-stretch ${className}`} data-testid={testId}>
      {rail && (
        <TokenBox
          width={railWidth}
          className={`shrink-0 border-r border-border bg-muted/40 ${railClassName}`}
          data-appframe-slot="rail"
        >
          {rail}
        </TokenBox>
      )}

      <div className="min-w-0 flex-1 bg-background">{canvas}</div>

      {panel && (
        <TokenBox
          width={panelWidth}
          className={`shrink-0 border-l border-border bg-muted/20 ${panelClassName}`}
          data-appframe-slot="panel"
        >
          {panel}
        </TokenBox>
      )}
    </div>
  );
}
